/**
 * Calentamiento del número: lo que la página dice que no viene armado del backend (`GET /calentamiento`).
 *
 * Los arreglos del backend le hablan al equipo: «completalo en la consola de Twilio», «marcalo en Configuración».
 * Una persona de un cliente no entra a Twilio ni a Configuración, y un paso que no puede hacer le enseña a
 * ignorar la lista. Para ella, esos dos pasos dicen qué hace ella y qué le pasa al equipo de Deenex.
 */

import type { Chequeo, EstadoCalentamiento, PasoCalentamiento, PasoPlanSubida } from "../types";
import { fechaHora, num } from "../../campana-nueva/utils/format";
import { fechaNegocio } from "./conversaciones";

/**
 * Cómo se ve un paso de «Lo que mira Meta» (auditoría de diseño del 18/10/2026: sin número, los 6 iban con el mismo
 * triángulo ámbar): hecho, falta (en rojo si frena la subida) o se mide después (todavía no se puede medir: no es algo
 * que la marca tenga que hacer).
 */
export type EstadoVisiblePaso = "hecho" | "falta" | "frena" | "despues";
export function estadoVisiblePaso(p: Pick<PasoCalentamiento, "estado" | "sinMedir">): EstadoVisiblePaso {
  if (p.estado === "ok") return "hecho";
  if (p.estado === "error") return "frena";
  return p.sinMedir ? "despues" : "falta";
}

/** El límite de Meta en palabras: «2.000 personas por día», «Sin límite» o «Sin dato». */
/**
 * El subtítulo de «Nivel de Meta»: de dónde salió el número. Con el que cargó el equipo (Twilio dice «Unavailable»), lo
 * dice: «Leído de WhatsApp» sería falso. Sin `origenLimite` (un backend anterior), como siempre.
 */
export function subtituloNivel(e: Pick<EstadoCalentamiento, "actualizadoAt" | "origenLimite">): string | undefined {
  if (e.origenLimite?.fuente === "equipo") {
    const dia = e.origenLimite.cargadoAt ? fechaNegocio(e.origenLimite.cargadoAt) : "";
    return `Lo cargó el equipo desde WhatsApp Manager${dia ? ` el ${dia}` : ""}: Twilio todavía no lo informa`;
  }
  return e.actualizadoAt ? `Leído de WhatsApp el ${fechaHora(e.actualizadoAt)} (se lee cada hora)` : undefined;
}

export function textoNivel(n: Pick<EstadoCalentamiento["nivel"], "limite" | "ilimitado">): string {
  if (n.ilimitado) return "Sin límite";
  return typeof n.limite === "number" ? `${num(n.limite)} personas por día` : "Sin dato";
}

/** El nivel siguiente: «10.000», «ilimitado» o «» (no hay siguiente o no se conoce). */
export function textoSiguiente(s: EstadoCalentamiento["nivel"]["siguiente"]): string {
  if (s === "ilimitado") return "ilimitado";
  return typeof s === "number" ? num(s) : "";
}

/**
 * La fila de 7 días sirve de 2.000 en adelante: en 250, lo que sube a 2.000 es verificar el negocio o las 2.000
 * entregas en 30 días (lo dice el paso «Uso del límite»), no la mitad por día.
 */
export function mostrarFilaDeDias(n: EstadoCalentamiento["nivel"]): boolean {
  return !n.ilimitado && n.mitad !== null && typeof n.limite === "number" && n.limite > 250;
}

/** «2026-10-10» → «sáb 10/10». Al mediodía UTC: el día no se corre con la zona del navegador. */
export function etiquetaDia(dia: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dia);
  if (!m) return dia;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  const semana = d.toLocaleDateString("es-AR", { weekday: "short", timeZone: "UTC" }).replace(".", "");
  return `${semana} ${m[3]}/${m[2]}`;
}

const ARREGLO_CLIENTE: Partial<Record<string, (p: PasoCalentamiento) => string>> = {
  portfolio: () =>
    "Verificá el negocio en el Business Manager de Meta (Centro de seguridad → Verificación del negocio) y avisale al equipo de Deenex. Es el salto más grande: de 250 a 2.000.",
  perfil: (p) =>
    p.detalle.startsWith("Todavía no se leyó")
      ? "Se lee solo cada hora. Si no aparece, avisale al equipo de Deenex."
      : "Pasale al equipo de Deenex lo que falta: lo cargan ellos. Quien recibe un mensaje de un perfil vacío lo bloquea más.",
  // Cargar una base es del equipo (`CargaNuevaPage` es `PaginaSoloEquipo`); «sumá contactos con el QR» sí lo hace el cliente.
  contactos: (p) =>
    String(p.arreglo).startsWith("Cargá")
      ? "Pasale al equipo de Deenex los clientes que dieron su consentimiento: los cargan ellos."
      : String(p.arreglo),
};

/** El arreglo que ve quien mira: el del backend para el equipo; para un cliente, el suyo en los pasos que hace el equipo. */
export function arregloPara(p: PasoCalentamiento, equipo: boolean): string {
  if (!p.arreglo) return "";
  if (equipo) return p.arreglo;
  const delCliente = ARREGLO_CLIENTE[p.id];
  return delCliente ? delCliente(p) : p.arreglo;
}

const nivel = (n: number | "ilimitado") => (n === "ilimitado" ? "sin límite" : num(n));

/** Un paso del plan en palabras: qué pide Meta y cuántos contactos hacen falta. */
export function textoPasoPlan(p: PasoPlanSubida): { titulo: string; detalle: string } {
  const titulo = `De ${nivel(p.desde)} a ${nivel(p.hacia)}`;
  if (p.via === "verificacion") return { titulo, detalle: "Esperar que Meta apruebe la verificación del negocio: no pide envíos." };
  if (p.via === "entregas") {
    return {
      titulo,
      detalle: `Entregar a ${num(p.personas)} personas distintas en 30 días: a ${num(p.porDia)} por día, ${p.dias} días como mínimo. O verificar el negocio en Meta.`,
    };
  }
  return { titulo, detalle: `${num(p.porDia)} personas distintas por día, ${p.dias} días seguidos. Hacen falta ${num(p.personas)} contactos con consentimiento.` };
}

/**
 * Hasta dónde llega la base de hoy: el último nivel de los pasos seguidos que alcanzan y los días mínimos. null si el
 * primero ya no alcanza (o no hay plan).
 */
export function hastaDondeLlega(plan: PasoPlanSubida[] | undefined): { hasta: number | "ilimitado"; dias: number } | null {
  let hasta: number | "ilimitado" | null = null;
  let dias = 0;
  for (const p of plan || []) {
    if (!p.alcanza) break;
    hasta = p.hacia;
    dias += Number(p.dias) || 0;
  }
  return hasta === null ? null : { hasta, dias };
}

/** La frase de los calientes del chequeo: «Salen primero 320 calientes: compraron en los últimos 30 días o ya te escribieron». */
export function textoCalientes(c: Chequeo["calientes"]): string {
  if (!c || !(c.cantidad > 0)) return "";
  const uno = c.cantidad === 1;
  const quienes = c.diasCaliente > 0
    ? uno ? `compró en los últimos ${num(c.diasCaliente)} días o ya te escribió` : `compraron en los últimos ${num(c.diasCaliente)} días o ya te escribieron`
    : uno ? "ya te escribió" : "ya te escribieron";
  return uno ? `Sale primero 1 caliente: ${quienes}.` : `Salen primero ${num(c.cantidad)} calientes: ${quienes}.`;
}

/**
 * El aviso del freno por calidad: qué está haciendo el motor solo. null sin freno (o un backend anterior). Con el freno
 * apagado y la calidad no alta, lo dice: nadie está frenando y la calidad puede seguir bajando.
 */
export function avisoFreno(e: Pick<EstadoCalentamiento, "freno" | "calidad">): { nivel: "pausa" | "mitad" | "apagado"; texto: string } | null {
  const f = e.freno;
  if (!f) return null;
  if (!f.activo) {
    return e.calidad === "YELLOW" || e.calidad === "RED"
      ? { nivel: "apagado", texto: "El freno por calidad está apagado: las campañas siguen al ritmo de siempre aunque la calidad baje." }
      : null;
  }
  if (f.nivel === "pausa") {
    const hasta = f.hasta ? ` hasta el ${fechaHora(f.hasta)}` : "";
    return {
      nivel: "pausa",
      texto: `Marketing en pausa${hasta}: Meta bajó la calidad del número a Baja. Las campañas de marketing esperan y siguen solas; las de utilidad salen a la mitad del cupo.`,
    };
  }
  if (f.nivel === "mitad") return { nivel: "mitad", texto: "Calidad media: las campañas salen a la mitad del cupo hasta que vuelva a Alta." };
  return null;
}
